import { FormationFile, FormationVideo, useGetFormationsMutation } from '@services';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

type Consultable<T> = T & {
    consulted: boolean;
};

export const useFormationTalkFilesPageVM = () => {
    const { categoryId, formationId } = useParams();
    const [pageTitle, setPageTitle] = useState<string>('');
    const [files, setFiles] = useState<Consultable<FormationFile>[]>([]);
    const [videos, setVideos] = useState<Consultable<FormationVideo>[]>([]);

    const [getFormations, { isLoading, data: formationsData }] = useGetFormationsMutation();

    useEffect(() => {
        if (categoryId && formationId) {
            getFormations({ categoryId: parseInt(categoryId) });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [categoryId, formationId]);

    useEffect(() => {
        if (formationsData && formationId) {
            const formation = formationsData.find((f) => f.id === parseInt(formationId));
            if (formation) {
                setPageTitle(formation.title);
                setFiles(formation.files.map((file) => ({ ...file, consulted: false })));
                setVideos(formation.formation_videos.map((video) => ({ ...video, consulted: false })));
            }
        }
    }, [formationsData, formationId]);

    const acknowledgeFile = (fileId: number) => {
        setFiles(files.map((file) => (file.id === fileId ? { ...file, consulted: true } : file)));
    };

    const acknowledgeVideo = (videoId: number) => {
        setVideos(videos.map((video) => (video.id === videoId ? { ...video, consulted: true } : video)));
    };

    const areFilesAcknowledged = useMemo(() => files.every((file) => file.consulted), [files]);
    const areVideosAcknowledged = useMemo(() => videos.every((video) => video.consulted), [videos]);

    const isEveryAssetAcknowledged = useMemo(() => {
        if (isLoading) {
            return false;
        }
        if (!files.length && !videos.length) {
            return false;
        }
        return areFilesAcknowledged && areVideosAcknowledged;
    }, [areFilesAcknowledged, areVideosAcknowledged, isLoading, files, videos]);

    return {
        categoryId,
        formationId,
        pageTitle,
        files,
        videos,
        isLoading,
        acknowledgeFile,
        acknowledgeVideo,
        areFilesAcknowledged,
        isEveryAssetAcknowledged
    };
};
